import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';

// Simple top navigation for public pages (no side nav)
export default function SimpleNavigation() {
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSolutionsOpen, setIsSolutionsOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-navy/95 backdrop-blur-md border-b border-white/10'
            : 'bg-transparent'
        }`}
      >
        <div className="w-full px-6 lg:px-12">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 bg-teal rounded-lg flex items-center justify-center">
                <span className="font-heading text-xl font-bold text-navy">C</span>
              </div>
              <div className="flex flex-col">
                <span className="font-heading text-xl font-bold text-white hidden sm:block">
                  cxood
                </span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {/* Solutions Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setIsSolutionsOpen(!isSolutionsOpen)}
                  className="flex items-center gap-1 text-slate hover:text-white transition-colors"
                >
                  Solutions
                  <ChevronDown className={`w-4 h-4 transition-transform ${isSolutionsOpen ? 'rotate-180' : ''}`} />
                </button>

                {isSolutionsOpen && (
                  <div className="absolute top-full left-0 mt-2 w-48 bg-surface border border-white/10 rounded-xl shadow-xl overflow-hidden">
                    <button
                      onClick={() => { scrollToSection('cxo-profiles'); setIsSolutionsOpen(false); }}
                      className="w-full text-left px-4 py-3 text-slate hover:text-white hover:bg-white/5 transition-colors"
                    >
                      Find a CXO
                    </button>
                    <button
                      onClick={() => { scrollToSection('insights'); setIsSolutionsOpen(false); }}
                      className="w-full text-left px-4 py-3 text-slate hover:text-white hover:bg-white/5 transition-colors"
                    >
                      Read Insights
                    </button>
                    <button
                      onClick={() => { scrollToSection('packages'); setIsSolutionsOpen(false); }}
                      className="w-full text-left px-4 py-3 text-slate hover:text-white hover:bg-white/5 transition-colors"
                    >
                      Engagement Models
                    </button>
                  </div>
                )}
              </div>

              <button
                onClick={() => scrollToSection('insights')}
                className="text-slate hover:text-white transition-colors"
              >
                Insights
              </button>

              <button
                onClick={() => scrollToSection('pricing')}
                className="text-slate hover:text-white transition-colors"
              >
                Pricing
              </button>

              <a
                href="mailto:sales@cxood.com"
                className="text-slate hover:text-white transition-colors"
              >
                Contact Sales
              </a>
            </div>

            {/* Desktop CTA Buttons */}
            <div className="hidden lg:flex items-center gap-4">
              <button
                onClick={() => navigate('/login')}
                className="text-slate hover:text-white transition-colors px-4 py-2"
              >
                Log in
              </button>
              <button
                onClick={() => navigate('/register')}
                className="bg-teal text-navy font-semibold px-6 py-2.5 rounded-xl hover:bg-teal-dark transition-colors"
              >
                Get Started
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden w-10 h-10 flex items-center justify-center"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6 text-white" />
              ) : (
                <Menu className="w-6 h-6 text-white" />
              )}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          <div className="absolute top-16 left-0 right-0 bg-surface border-b border-white/10 p-6">
            <div className="space-y-4">
              <button
                onClick={() => scrollToSection('cxo-profiles')}
                className="block w-full text-left text-slate hover:text-white transition-colors py-2"
              >
                Find a CXO
              </button>
              <button
                onClick={() => scrollToSection('insights')}
                className="block w-full text-left text-slate hover:text-white transition-colors py-2"
              >
                Insights
              </button>
              <button
                onClick={() => scrollToSection('pricing')}
                className="block w-full text-left text-slate hover:text-white transition-colors py-2"
              >
                Pricing
              </button>
              <button
                onClick={() => navigate('/login')}
                className="block w-full text-left text-slate hover:text-white transition-colors py-2"
              >
                Log in
              </button>
              <button
                onClick={() => navigate('/register')}
                className="w-full bg-teal text-navy font-semibold px-6 py-3 rounded-xl hover:bg-teal-dark transition-colors"
              >
                Get Started
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
// Build: Fri Mar 20 06:09:23 PM CST 2026
